package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.textDirection
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal data class EditorCompactSummaryModel(
    val source: String,
    val target: String,
    val sourceLocale: String,
    val targetLocale: String,
    val pluralCategory: String?,
    val qaSeverity: String,
)

internal object EditorCompactSummaryBounds {
    const val summaryMaxHeight = "min(25dvh, 144px)"
    const val bodyMinHeight = "min(220px, 40dvh)"
    const val collapsedSourceLines = 2
    const val collapsedTargetLines = 2
    const val collapsedQaLines = 1
}

internal fun requestSummaryCollapseFocus(
    onRequestFocus: (EditorFocusRef, EditorFocusRef?) -> Unit,
) {
    onRequestFocus(
        EditorFocusRef.ElementId(EDITOR_SUMMARY_TOGGLE_ID),
        EditorFocusRef.ElementId(EDITOR_UTILITY_CLOSE_ID),
    )
}

internal fun editorCompactSummary(
    row: KeyRow,
    draft: EditorDraft,
    targetLocale: String,
    category: PluralCategory?,
    findings: List<InlineQaFinding>,
): EditorCompactSummaryModel {
    val activeCategory = category?.takeIf { row.key.isPlural }
    val categoryForValue = activeCategory ?: PluralCategory.OTHER
    val qaSeverity = when {
        findings.any { it.severity == QaSeverity.ERROR } ->
            tr("editor.editor_compact_summary.error", "Error")
        findings.any { it.severity == QaSeverity.WARNING } ->
            tr("editor.editor_compact_summary.warning", "Warning")
        else -> tr("editor.editor_compact_summary.no_qa_issues", "No QA issues")
    }
    return EditorCompactSummaryModel(
        source = activeSourceValue(row, categoryForValue),
        target = activeTargetValue(row, draft, categoryForValue),
        sourceLocale = row.source.locale,
        targetLocale = targetLocale,
        pluralCategory = activeCategory?.displayName(),
        qaSeverity = qaSeverity,
    )
}

@Composable
internal fun EditorCompactSummary(
    model: EditorCompactSummaryModel,
    expanded: Boolean,
    onExpandedChange: (Boolean) -> Unit,
    onRequestFocus: (EditorFocusRef, EditorFocusRef?) -> Unit,
    bodyOwnsScroll: Boolean = false,
) {
    Column(
        EditorCompactSummaryStyle.toModifier()
            .fillMaxWidth()
            .minWidth(0.px)
            .then(
                if (bodyOwnsScroll) {
                    Modifier.styleModifier {
                        property("max-height", "none")
                        property("overflow", "visible")
                        property("padding-left", "0")
                        property("padding-right", "0")
                    }
                } else {
                    Modifier
                },
            )
            .attr("data-editor-compact-summary", "true")
            .attr("role", "region")
            .attr(
                "aria-label",
                t("editor.editor_compact_summary.active_translation_summary", "Active translation summary"),
            ),
    ) {
        Column(
            EditorCompactSummaryValuesStyle.toModifier()
                .fillMaxWidth()
                .minWidth(0.px)
                .then(
                    if (bodyOwnsScroll) {
                        Modifier.styleModifier { property("overflow-y", "visible") }
                    } else {
                        Modifier
                    },
                ),
        ) {
            model.pluralCategory?.let { category ->
                Span(EditorCompactSummaryCategoryStyle.toModifier().toAttrs()) { Text(category) }
            }
            SummaryValue(
                label = t("editor.editor_compact_summary.source", "Source"),
                value = model.source,
                fallback = t("editor.editor_compact_summary.no_source_text", "No source text"),
                lines = EditorCompactSummaryBounds.collapsedSourceLines,
                expanded = expanded,
                allowOwnScroll = !bodyOwnsScroll,
                hook = "data-editor-summary-source",
                locale = model.sourceLocale,
            )
            SummaryValue(
                label = t("editor.editor_compact_summary.target", "Target"),
                value = model.target,
                fallback = t("editor.editor_compact_summary.not_translated", "Not translated"),
                lines = EditorCompactSummaryBounds.collapsedTargetLines,
                expanded = expanded,
                allowOwnScroll = !bodyOwnsScroll,
                hook = "data-editor-summary-target",
                locale = model.targetLocale,
            )
            SummaryValue(
                label = t("editor.editor_compact_summary.qa", "QA"),
                value = model.qaSeverity,
                fallback = t("editor.editor_compact_summary.no_qa_issues", "No QA issues"),
                lines = EditorCompactSummaryBounds.collapsedQaLines,
                expanded = expanded,
                allowOwnScroll = !bodyOwnsScroll,
                hook = "data-editor-summary-qa",
            )
        }
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            LButton(
                label = if (expanded) t("editor.editor_compact_summary.show_less", "Show less") else t("editor.editor_compact_summary.show_full_summary", "Show full summary"),
                onClick = {
                    val nextExpanded = !expanded
                    onExpandedChange(nextExpanded)
                    if (!nextExpanded) requestSummaryCollapseFocus(onRequestFocus)
                },
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                modifier = EditorCoarseTargetStyle.toModifier()
                    .attr("id", EDITOR_SUMMARY_TOGGLE_ID)
                    .attr("data-editor-coarse-target", "tool")
                    .attr("aria-expanded", expanded.toString()),
            )
        }
    }
}

@Composable
private fun SummaryValue(
    label: String,
    value: String,
    fallback: String,
    lines: Int,
    expanded: Boolean,
    allowOwnScroll: Boolean,
    hook: String,
    locale: String? = null,
) {
    // The stable half of `hook` names the row, and both the element id and the DOM contract are
    // built from it. The contract used to be `label.lowercase()` — copy that now arrives from the
    // catalogue, so under a German interface the hook every surface test and every controller
    // lookup reads would have become `quelle` / `ziel`. A data hook cannot be a translation.
    val slug = hook.removePrefix("data-editor-summary-")
    val labelId = "lingvo-editor-summary-$slug-label"
    val contentLocale = locale?.takeIf { value.isNotBlank() && it.isNotBlank() }
    Span(
        EditorCompactSummaryCategoryStyle.toModifier()
            .attr("id", labelId)
            .attr("data-editor-summary-label", slug)
            .toAttrs(),
    ) {
        Text(label)
    }
    Div(
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .styleModifier {
                property("overflow-wrap", "anywhere")
                property("white-space", "pre-wrap")
                property("user-select", "text")
                property("unicode-bidi", if (contentLocale != null) "isolate" else "normal")
                if (expanded && allowOwnScroll) {
                    property("overflow-y", "auto")
                } else if (!expanded) {
                    property("display", "-webkit-box")
                    property("-webkit-box-orient", "vertical")
                    property("-webkit-line-clamp", lines.toString())
                    property("overflow", "hidden")
                }
            }
            .toAttrs {
                attr(hook, "true")
                attr("aria-labelledby", labelId)
                if (contentLocale != null) {
                    attr("lang", contentLocale)
                    attr("dir", textDirection(contentLocale).html)
                }
            },
    ) {
        Text(value.ifBlank { fallback })
    }
}
